import React, { useState, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC, s_GB } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

function playNote(freq, duration = 0.5) {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine'; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.35, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(); osc.stop(ac.currentTime + duration);
    setTimeout(() => ac.close(), (duration + 0.1) * 1000);
  } catch {}
}

const ROUNDS = 10;

function PitchMatchGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('start'); // start | listen1 | listen2 | answer | result | done
  const [correct, setCorrect] = useState(null);
  const [freqA, setFreqA] = useState(null);
  const [freqB, setFreqB] = useState(null);
  const timerRef = useRef(null);

  const BASE_FREQS = [261, 294, 330, 349, 392, 440, 494, 523, 587, 659];

  const startRound = () => {
    const base = BASE_FREQS[Math.floor(Math.random() * BASE_FREQS.length)];
    const diff = (Math.random() < 0.5 ? 1 : -1) * (20 + Math.floor(Math.random() * 80));
    const a = base;
    const b = base + diff;
    setFreqA(a); setFreqB(b); setCorrect(null); setPhase('listen1');
    playNote(a, 0.6);
    timerRef.current = setTimeout(() => {
      setPhase('listen2');
      playNote(b, 0.6);
      timerRef.current = setTimeout(() => setPhase('answer'), 800);
    }, 900);
  };

  const answer = (guess) => {
    if (phase !== 'answer') return;
    const realAnswer = freqB > freqA ? 'higher' : 'lower';
    const isRight = guess === realAnswer;
    setCorrect(isRight);
    setPhase('result');
    if (isRight) { setScore(s => s + 1); playNote(880, 0.15); }
    else { playNote(220, 0.3); }
    timerRef.current = setTimeout(() => {
      const next = round + 1;
      if (next >= ROUNDS) { setPhase('done'); saveGameScore('pitchmatch', Math.round((score + (isRight ? 1 : 0)) / ROUNDS * 100)); }
      else { setRound(next); startRound(); }
    }, 1000);
  };

  if (phase === 'done') {
    return (
      <div>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>■ {nl ? 'Stop' : 'Stop'}</button>
        <div style={{ ...s_GC({ padding: 24, textAlign: 'center' }) }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🎵</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Klaar!' : 'Done!'}</div>
          <div style={{ color: _T.accent, fontSize: 32, fontWeight: 700 }}>{score}/{ROUNDS}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 16 }}>{nl ? 'correcte antwoorden' : 'correct answers'}</div>
          <button style={s_PB({ width: '100%' })} onClick={() => { setRound(0); setScore(0); setPhase('start'); }}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>■ {nl ? 'Stop' : 'Stop'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? `Ronde ${round + 1}/${ROUNDS}` : `Round ${round + 1}/${ROUNDS}`}</span>
        <span style={{ color: _T.accent, fontSize: 12, fontWeight: 700 }}>{score} ✓</span>
      </div>

      <div style={{ ...s_GC({ padding: 28, textAlign: 'center', marginBottom: 14 }) }}>
        {phase === 'start' && <><div style={{ fontSize: 48 }}>🎵</div><div style={{ color: C.silver, fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{nl ? 'Je hoort twee tonen. Is de tweede hoger of lager?' : 'You hear two tones. Is the second higher or lower?'}</div></>}
        {phase === 'listen1' && <><div style={{ fontSize: 48 }} className="popIn">🎵</div><div style={{ color: _T.accent, fontWeight: 700, marginTop: 8 }}>{nl ? 'Toon 1...' : 'Tone 1...'}</div></>}
        {phase === 'listen2' && <><div style={{ fontSize: 48 }} className="popIn">🎵</div><div style={{ color: C.orange, fontWeight: 700, marginTop: 8 }}>{nl ? 'Toon 2...' : 'Tone 2...'}</div></>}
        {phase === 'answer' && <><div style={{ fontSize: 36, marginBottom: 8 }}>🤔</div><div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>{nl ? 'Was de tweede toon hoger of lager?' : 'Was the second tone higher or lower?'}</div></>}
        {phase === 'result' && <><div style={{ fontSize: 48 }}>{correct ? '✅' : '❌'}</div><div style={{ color: correct ? C.teal : C.red, fontWeight: 700, marginTop: 8 }}>{correct ? (nl ? 'Goed!' : 'Correct!') : (nl ? 'Fout!' : 'Wrong!')}</div><div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>{freqB > freqA ? (nl ? 'De tweede was hoger' : 'Second was higher') : (nl ? 'De tweede was lager' : 'Second was lower')}</div></>}
      </div>

      {phase === 'start' && <button style={s_PB({ width: '100%' })} onClick={startRound}>{nl ? 'Start' : 'Start'}</button>}
      {phase === 'answer' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button style={s_GB({ padding: '16px 0', fontSize: 16, width: '100%' })} onClick={() => answer('higher')}>⬆ {nl ? 'Hoger' : 'Higher'}</button>
          <button style={s_GB({ padding: '16px 0', fontSize: 16, width: '100%' })} onClick={() => answer('lower')}>⬇ {nl ? 'Lager' : 'Lower'}</button>
        </div>
      )}
    </div>
  );
}

export default PitchMatchGame;
